import React, {FC, memo, useRef, useCallback, useEffect, useState} from "react";
import {Modal, Descriptions, Button, Spin, Typography, Tag, Space, Alert, Radio, Collapse} from "antd";
import {PrinterOutlined, PaperClipOutlined} from "@ant-design/icons";
import {useGetEmailNotificationByIdQuery, useGetMjmlTemplateByIdQuery} from "../../../generated/graphql";
import {renderMessageStatus, RenderBookingNumber} from "../../../views/TableColumnRenderers/TableColumnRenderers";
import {toMjml} from "../../../views/MjmlPreview/MJMLTemplates/toMjml";
import EmailTrackingTimeline from "./EmailTrackingTimeline";
import i18n from "../../../services/i18n";
import css from "./EmailDetailModal.less";

const {Title} = Typography;

// Mailjet-Templates, die serverseitig einen Kursdetails-Block (Kursname + Kurstermine-Tabelle)
// in die versendete Mail einfügen. Für diese Mails wird kein HTML gespeichert — die Vorschau
// zeigt nur die Textbausteine und der Block fehlt darin systembedingt.
const MAILJET_TEMPLATES_WITH_COURSE_DETAILS = new Set([
    2046014, // BookingConfirmation
    2123004, // BookingCancellation
    2123003, // InvoiceSettlement
    2473686, // WaitingListConfirmation
]);

const getMessageStatusText = (messageStatus: string | undefined | null): string => {
    switch (messageStatus) {
        case "unknown":
            return "Unbekannt";
        case "queued":
            return "In Warteschlange";
        case "sent":
            return "Zugestellt";
        case "opened":
            return "Geöffnet";
        case "clicked":
            return "Angeklickt";
        case "bounce":
            return "Rückläufer";
        case "spam":
            return "Spam";
        case "unsub":
            return "Abbestellt";
        case "blocked":
            return "Geblockt";
        case "hardbounced":
            return "Rückläufer (Hard Bounce)";
        case "softbounced":
            return "Rückläufer (Soft Bounce)";
        default:
            return messageStatus || "-";
    }
};

// Kurstermin-Zeile aus email_notification.courseDetailsSnapshot (JSON, Stand zum
// Versandzeitpunkt); location/city fehlen beim Rechnungs-/Storno-Format.
type CourseDetailsSnapshotLesson = {
    num: number;
    dow: string;
    date: string;
    time: string;
    location?: string;
    city?: string;
};

// Beim Versand eingefrorene Render-Variablen (email_notification.renderVariablesSnapshot).
type RenderVariablesSnapshot = {
    subject?: string;
    headerText?: string;
    footerText?: string;
    signature?: string;
    course?: {courseNumber?: string; name?: string; subTitle?: string};
};

type EmailDetailModalProps = {
    notificationId: string | null;
    onClose: () => void;
};

const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const EmailDetailModal: FC<EmailDetailModalProps> = ({notificationId, onClose}) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Vorschau-Modus: Text-Ansicht (Standard) oder nachgerendertes Mailjet-Layout.
    // Der State lebt in DIESER Komponente, nicht in den Modal-Children —
    // destroyOnClose räumt ihn also NICHT auf (#513). Beim Öffnen einer
    // (anderen) Mail explizit auf die Text-Ansicht zurücksetzen; das gated
    // zugleich die MJML-Abfrage wieder auf eine bewusste Nutzerwahl.
    const [previewMode, setPreviewMode] = useState<"text" | "layout">("text");

    useEffect(() => {
        if (notificationId) {
            setPreviewMode("text");
        }
    }, [notificationId]);

    const {data, loading} = useGetEmailNotificationByIdQuery({
        variables: {id: notificationId ?? ""},
        skip: !notificationId,
        fetchPolicy: "cache-and-network",
    });

    const notification = data?.emailNotificationById;

    // Layout-Ansicht: iframe auf die volle Inhaltshöhe wachsen lassen, damit die Mail
    // ohne inneres Scrollen komplett sichtbar ist (sandbox erlaubt same-origin-Zugriff).
    const handleLayoutFrameLoad = useCallback((event: React.SyntheticEvent<HTMLIFrameElement>) => {
        const frame = event.currentTarget;
        const contentHeight = frame.contentDocument?.body?.scrollHeight;

        if (contentHeight) {
            frame.style.height = `${contentHeight + 32}px`;
        }
    }, []);

    // Etappe 3: echtes Mailjet-Layout nachrendern, wenn beide Versand-Snapshots vorliegen
    // (Textbausteine + Kurstermine) und die Mail über eines der vier bekannten
    // Kursdetails-Templates lief. Alt-Mails ohne Snapshots fallen auf die Text-Ansicht zurück.
    const renderVariablesSnapshot = (notification?.renderVariablesSnapshot ?? null) as RenderVariablesSnapshot | null;
    const snapshotLessons = (notification?.courseDetailsSnapshot ?? null) as Array<CourseDetailsSnapshotLesson> | null;
    const canRenderTrueLayout = Boolean(
        notification &&
            !notification.html &&
            notification.mailjetTemplateId != null &&
            MAILJET_TEMPLATES_WITH_COURSE_DETAILS.has(notification.mailjetTemplateId) &&
            renderVariablesSnapshot?.course &&
            snapshotLessons &&
            snapshotLessons.length > 0,
    );

    // MJML erst laden, wenn die Layout-Ansicht tatsächlich geöffnet wird.
    const {data: mjmlData, loading: mjmlLoading} = useGetMjmlTemplateByIdQuery({
        variables: {templateId: String(notification?.mailjetTemplateId ?? "")},
        skip: !canRenderTrueLayout || previewMode !== "layout",
    });

    // Fehlgeschlagenes MJML-Laden oder -Rendern → null, Aufrufer fällt auf Text-Ansicht zurück.
    const trueLayoutHtml = (() => {
        const mjmlTemplate = mjmlData?.getEmailTemplateById?.Data;
        if (!canRenderTrueLayout || !renderVariablesSnapshot || !snapshotLessons || typeof mjmlTemplate !== "string") {
            return null;
        }

        try {
            return toMjml({
                mjmlTemplate,
                variables: {
                    subject: renderVariablesSnapshot.subject ?? "",
                    headerText: renderVariablesSnapshot.headerText ?? "",
                    footerText: renderVariablesSnapshot.footerText ?? "",
                    signature: renderVariablesSnapshot.signature ?? "",
                },
                courseLessons: snapshotLessons.map((lesson) => ({
                    ...lesson,
                    location: lesson.location ?? "",
                    city: lesson.city ?? "",
                })),
                course: renderVariablesSnapshot.course,
            });
        } catch {
            return null;
        }
    })();

    const handlePrint = useCallback(() => {
        if (!notification) return;

        const content = notification.html || notification.text;
        if (!content) return;

        // Metadaten-Zeilen sammeln
        const metaRows: Array<{label: string; value: string}> = [];
        if (notification.receiverName) {
            metaRows.push({label: i18n.containers.notifications.EmailDetailModal.receiver(), value: notification.receiverName});
        }
        metaRows.push({label: i18n.containers.notifications.EmailDetailModal.email(), value: notification.email});
        metaRows.push({label: i18n.containers.notifications.EmailDetailModal.subject(), value: notification.subject || ""});
        metaRows.push({
            label: i18n.containers.notifications.EmailDetailModal.sentAt(),
            value: notification.createDate ? formatDate(notification.createDate) : "-",
        });
        if (notification.templateName) {
            metaRows.push({label: i18n.containers.notifications.EmailDetailModal.template(), value: notification.templateName});
        }
        metaRows.push({label: i18n.containers.notifications.EmailDetailModal.status(), value: getMessageStatusText(notification.messageStatus?.Status)});
        if (notification.booking) {
            const b = notification.booking;
            const bookingNum = [b.bookingNumberPrefix, b.bookingNumber].filter(Boolean).join("-");
            metaRows.push({label: i18n.containers.notifications.EmailDetailModal.linkedBooking(), value: bookingNum});
        }
        if (notification.attachments) {
            const atts = notification.attachments as Array<{fileName?: string}>;
            if (atts.length > 0) {
                const names = atts.map((a, idx) => a.fileName || `Anhang ${idx + 1}`).join(", ");
                metaRows.push({label: i18n.containers.notifications.EmailDetailModal.attachments(), value: names});
            }
        }

        // HTML-Tabelle für Metadaten
        const metaHtml = metaRows
            .map(
                (r) =>
                    `<tr><td style="padding: 6px 12px; font-weight: 600; white-space: nowrap; vertical-align: top; border-bottom: 1px solid #eee;">${r.label}</td><td style="padding: 6px 12px; border-bottom: 1px solid #eee;">${r.value}</td></tr>`,
            )
            .join("");

        // E-Mail-Body
        const bodyHtml = notification.html
            ? content
            : `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 14px; color: #333; padding: 16px;">${content}</div>`;

        const printDoc = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 20px; color: #333; }
    table { border-collapse: collapse; width: 100%; margin-bottom: 16px; }
    hr { border: none; border-top: 1px solid #ccc; margin: 16px 0; }
    @media print { body { margin: 10mm; } }
</style></head><body>
<table>${metaHtml}</table>
<hr/>
${bodyHtml}
</body></html>`;

        // Versteckten iframe erstellen, drucken, aufräumen
        const printFrame = document.createElement("iframe");
        printFrame.style.position = "fixed";
        printFrame.style.left = "-9999px";
        printFrame.style.width = "0";
        printFrame.style.height = "0";
        document.body.appendChild(printFrame);

        const frameDoc = printFrame.contentDocument || printFrame.contentWindow?.document;
        if (frameDoc) {
            frameDoc.open();
            frameDoc.write(printDoc);
            frameDoc.close();
        }

        // Kurz warten bis iframe gerendert ist, dann drucken
        setTimeout(() => {
            printFrame.contentWindow?.print();
            document.body.removeChild(printFrame);
        }, 250);
    }, [notification]);

    const renderMetadata = () => {
        if (!notification) return null;

        return (
            <Descriptions column={1} size="small" bordered>
                {notification.receiverName && <Descriptions.Item label={i18n.containers.notifications.EmailDetailModal.receiver()}>{notification.receiverName}</Descriptions.Item>}
                <Descriptions.Item label={i18n.containers.notifications.EmailDetailModal.email()}>{notification.email}</Descriptions.Item>
                <Descriptions.Item label={i18n.containers.notifications.EmailDetailModal.subject()}>{notification.subject}</Descriptions.Item>
                <Descriptions.Item label={i18n.containers.notifications.EmailDetailModal.sentAt()}>{notification.createDate ? formatDate(notification.createDate) : "-"}</Descriptions.Item>
                {notification.templateName && <Descriptions.Item label={i18n.containers.notifications.EmailDetailModal.template()}>{notification.templateName}</Descriptions.Item>}
                <Descriptions.Item label={i18n.containers.notifications.EmailDetailModal.status()}>{renderMessageStatus(notification.messageStatus)}</Descriptions.Item>
                {notification.booking && (
                    <Descriptions.Item label={i18n.containers.notifications.EmailDetailModal.linkedBooking()}>
                        <RenderBookingNumber
                            bookingNumberPrefix={notification.booking.bookingNumberPrefix}
                            bookingNumber={notification.booking.bookingNumber}
                            bookingType={notification.booking.bookingType}
                            canceled={notification.booking.canceled}
                            bookingId={notification.booking.id}
                            invoiceId={notification.booking.invoice?.id}
                            reversalInvoiceId={notification.booking.reversalInvoice?.id}
                        />
                    </Descriptions.Item>
                )}
                {notification.attachments && (notification.attachments as Array<{fileName?: string; contentType?: string}>).length > 0 && (
                    <Descriptions.Item label={i18n.containers.notifications.EmailDetailModal.attachments()}>
                        <Space direction="vertical" size={2}>
                            {(notification.attachments as Array<{fileName?: string; contentType?: string}>).map((attachment, index) => (
                                <Tag key={index} icon={<PaperClipOutlined />}>
                                    {attachment.fileName ? attachment.fileName : `Anhang ${index + 1}`}
                                </Tag>
                            ))}
                        </Space>
                    </Descriptions.Item>
                )}
            </Descriptions>
        );
    };

    const renderCourseDetailsSnapshot = (lessons: Array<CourseDetailsSnapshotLesson>) => {
        const hasLocation = lessons.some((lesson) => lesson.location);

        // Eingeklappt als Standard — Kurse können sehr viele Termine haben und die
        // Tabelle würde die eigentliche Mail-Vorschau sonst weit nach unten schieben.
        return (
            <Collapse className={css.courseDetailsSnapshot}>
                <Collapse.Panel key="lessons" header={`${i18n.containers.notifications.EmailDetailModal.courseDetailsSnapshotTitle()} (${lessons.length})`}>
                    <table>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{i18n.containers.notifications.EmailDetailModal.snapshotDow()}</th>
                                <th>{i18n.containers.notifications.EmailDetailModal.snapshotDate()}</th>
                                <th>{i18n.containers.notifications.EmailDetailModal.snapshotTime()}</th>
                                {hasLocation && <th>{i18n.containers.notifications.EmailDetailModal.snapshotLocation()}</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {lessons.map((lesson) => (
                                <tr key={lesson.num}>
                                    <td>{lesson.num}</td>
                                    <td>{lesson.dow}</td>
                                    <td>{lesson.date}</td>
                                    <td>{lesson.time}</td>
                                    {hasLocation && <td>{[lesson.location, lesson.city].filter(Boolean).join(" | ")}</td>}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Collapse.Panel>
            </Collapse>
        );
    };

    const renderEmailPreview = () => {
        if (!notification) return null;

        // Layout-Ansicht (Etappe 3, per Umschalter): echtes Mailjet-Layout aus den
        // Versand-Snapshots — Kursdetails-Box und Termin-Tabelle stecken im Layout.
        if (previewMode === "layout" && canRenderTrueLayout) {
            if (trueLayoutHtml) {
                return (
                    <div className={css.emailPreviewContainer}>
                        <iframe
                            ref={iframeRef}
                            srcDoc={trueLayoutHtml}
                            title="E-Mail Preview"
                            className={`${css.emailPreviewIframe} ${css.emailPreviewIframeAuto}`}
                            sandbox="allow-same-origin allow-modals"
                            onLoad={handleLayoutFrameLoad}
                        />
                    </div>
                );
            }

            if (mjmlLoading) {
                return (
                    <div className={css.layoutLoading}>
                        <Spin />
                    </div>
                );
            }
            // MJML nicht ladbar/renderbar → unten in die Text-Ansicht durchfallen.
        }

        const content = notification.html || notification.text;

        if (content) {
            const srcDoc = notification.html
                ? content
                : `<html><body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 14px; color: #333; padding: 16px;">${content}</body></html>`;

            // Beim Versand gespeicherter Kurstermin-Snapshot (Stand zum Versandzeitpunkt) —
            // damit zeigt die Vorschau den vom Mailjet-Template eingefügten Block nach.
            const courseDetailsSnapshot = (notification.courseDetailsSnapshot ?? null) as Array<CourseDetailsSnapshotLesson> | null;
            const hasSnapshot = !notification.html && Boolean(courseDetailsSnapshot && courseDetailsSnapshot.length > 0);

            // Text-Fallback einer ALTEN Template-Mail mit Kursdetails-Block (ohne Snapshot):
            // Der Block wurde von Mailjet beim Versand eingefügt und ist im gespeicherten
            // Text nicht enthalten — nur dann bleibt der erklärende Hinweis.
            const showCourseDetailsHint = !notification.html && !hasSnapshot && notification.mailjetTemplateId != null && MAILJET_TEMPLATES_WITH_COURSE_DETAILS.has(notification.mailjetTemplateId);

            return (
                <>
                    {showCourseDetailsHint && (
                        <Alert
                            type="info"
                            showIcon
                            className={css.previewHint}
                            message={i18n.containers.notifications.EmailDetailModal.courseDetailsHintTitle()}
                            description={i18n.containers.notifications.EmailDetailModal.courseDetailsHintDescription()}
                        />
                    )}
                    {hasSnapshot && courseDetailsSnapshot && renderCourseDetailsSnapshot(courseDetailsSnapshot)}
                    <div className={css.emailPreviewContainer}>
                        <iframe ref={iframeRef} srcDoc={srcDoc} title="E-Mail Preview" className={css.emailPreviewIframe} sandbox="allow-same-origin allow-modals" />
                    </div>
                </>
            );
        }

        return <div className={css.textFallback}>{i18n.containers.notifications.EmailDetailModal.noHtmlContent()}</div>;
    };

    return (
        <Modal
            title={i18n.containers.notifications.EmailDetailModal.title()}
            visible={Boolean(notificationId)}
            onCancel={onClose}
            width={1000}
            className={css.emailDetailModal}
            destroyOnClose
            footer={
                <Space>
                    <Button icon={<PrinterOutlined />} onClick={handlePrint} disabled={!notification?.html && !notification?.text}>
                        {i18n.containers.notifications.EmailDetailModal.print()}
                    </Button>
                    <Button onClick={onClose}>{i18n.containers.notifications.EmailDetailModal.close()}</Button>
                </Space>
            }
        >
            <Spin spinning={loading}>
                {renderMetadata()}

                <div className={css.previewHeader}>
                    <Title level={5} className={css.sectionTitle}>
                        {i18n.containers.notifications.EmailDetailModal.emailPreview()}
                    </Title>
                    {canRenderTrueLayout && (
                        <Radio.Group size="small" buttonStyle="solid" value={previewMode} onChange={(event) => setPreviewMode(event.target.value)}>
                            <Radio.Button value="text">{i18n.containers.notifications.EmailDetailModal.previewModeText()}</Radio.Button>
                            <Radio.Button value="layout">{i18n.containers.notifications.EmailDetailModal.previewModeLayout()}</Radio.Button>
                        </Radio.Group>
                    )}
                </div>
                {renderEmailPreview()}

                <Title level={5} className={css.sectionTitle}>
                    {i18n.containers.notifications.EmailDetailModal.trackingHistory()}
                </Title>
                <EmailTrackingTimeline messageHistory={notification?.messageHistory} messageStatus={notification?.messageStatus} createDate={notification?.createDate} />
            </Spin>
        </Modal>
    );
};

export default memo(EmailDetailModal);
